import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip/Tooltip';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteCircleIcon from '@/assets/delete-circle.svg?react';
import WarningTriangleIcon from '@/assets/warning-triangle.svg?react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUploadContext } from '@/contexts/UploadContext';
import { FileType, PartnerSchema } from '@/lib/types';
import Select from '@/components/Select/Select';
import {
  fetchFilters,
  fetchPartners,
  postMarketingAssetsPrecheck,
  postMarketingAssetsUploadSessions,
} from '@/lib/api';
import DropZoneBig, {
  type PickedFile,
} from '@/components/DropZoneBig/DropZoneBig';
import { FileTypesMarketing } from '@/constants/FileTypesMarketing';
import { MarketingUploadAcceptedExtensions } from '@/constants/UploadAcceptedExtensions';
import { mapConfigToOptions } from '@/lib/listOptions';
import { formatFileSize } from '@/lib/fileSize';
import { folderPartOf, withRenamedFile } from '@/lib/relativePath';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import FileIcon from '@/components/FileExplorer/FileIcon';
import FileStatusBadge from '@/components/ComplianceDocumentsUploadModal/FileStatusBadge';
import EditableFileName, {
  renameFile,
} from '@/components/ComplianceDocumentsUploadModal/EditableFileName';
import { classifyUploadError } from '@/lib/uploadErrors';

type FileListItemStatus =
  | 'awaitingAssetType'
  | 'loading'
  | 'pending'
  | 'duplicate'
  | 'inProgress'
  | 'error';

type FileListItem = {
  id: string;
  file: File;
  /** Path inside the picked folder (`C/d.txt`). */
  relativePath: string;
  status: FileListItemStatus;
  errorMessage?: string;
};

const fileNameToIconType = (name: string): string | undefined => {
  const i = name.lastIndexOf('.');
  if (i === -1 || i === name.length - 1) return undefined;
  return name.slice(i + 1).toLowerCase();
};

const getFileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 || dot === name.length - 1
    ? ''
    : name.slice(dot + 1).toLowerCase();
};

/**
 * Maps a rejection from precheck or upload-sessions onto a row state. `duplicate` and `inProgress`
 * are different problems: the first needs a new name, the second only needs time.
 */
const uploadErrorToFileStatus = (
  error?: string,
): Pick<FileListItem, 'status' | 'errorMessage'> => {
  switch (classifyUploadError(error)) {
    case undefined:
      return { status: 'pending', errorMessage: undefined };
    case 'duplicate':
      return { status: 'duplicate', errorMessage: undefined };
    case 'inProgress':
      return { status: 'inProgress', errorMessage: error };
    default:
      return { status: 'error', errorMessage: error };
  }
};

const MarketingAssetsUploadModal = ({
  gameId,
  gameGroup,
  open,
  onClose,
  onSave,
  initialFile,
}: {
  gameId: number;
  gameGroup?: string | null;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  initialFile?: File | null;
}) => {
  const [selectedAssetType, setSelectedAssetType] = useState<
    string | undefined
  >();
  const [selectedOperator, setSelectedOperator] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [fileItems, setFileItems] = useState<FileListItem[]>([]);
  const { addUpload } = useUploadContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPopperReady, setIsPopperReady] = useState(false);
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const intl = useIntl();

  const {
    mutate: mutateUploadSessions,
    isPending: isUploadSessionsPending,
    reset: resetUploadSessionsMutation,
  } = useMutation({
    mutationFn: postMarketingAssetsUploadSessions,
  });

  const { mutate: mutatePrecheck } = useMutation({
    mutationFn: postMarketingAssetsPrecheck,
  });

  const { data: filtersData, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
    enabled: open,
  });

  const precheckRequestIdRef = useRef(0);

  useEffect(() => {
    if (!open) {
      resetUploadSessionsMutation();
    }
  }, [open, resetUploadSessionsMutation]);

  const fileItemsKey = fileItems
    .map((item) => `${item.id}:${item.file.name}`)
    .join('|');

  useEffect(() => {
    if (fileItems.length === 0) return;

    if (!selectedAssetType) {
      setFileItems((prev) =>
        prev.map((item) =>
          item.status === 'awaitingAssetType'
            ? item
            : {
                ...item,
                status: 'awaitingAssetType',
                errorMessage: undefined,
              },
        ),
      );
      return;
    }

    const requestId = ++precheckRequestIdRef.current;
    const snapshotItems = fileItems;
    const file_type = selectedAssetType as FileType;
    const game_ids = [gameId];
    const partner_ids = selectedOperator ? [selectedOperator.id] : [];

    setFileItems((prev) =>
      prev.map((item) => ({
        ...item,
        status: 'loading',
        errorMessage: undefined,
      })),
    );

    mutatePrecheck(
      {
        files: snapshotItems.map((item) => ({
          name: item.relativePath,
          extension: getFileExtension(item.file.name),
          file_type,
          game_ids,
          partner_ids,
        })),
      },
      {
        onSuccess: (data) => {
          if (precheckRequestIdRef.current !== requestId) return;
          setFileItems((prev) =>
            prev.map((item) => {
              const snapshotIndex = snapshotItems.findIndex(
                (s) => s.id === item.id,
              );
              if (snapshotIndex === -1) return item;
              const result = data.results.find(
                (r) => r.index === snapshotIndex,
              );
              return {
                ...item,
                ...uploadErrorToFileStatus(result?.error),
              };
            }),
          );
        },
        onError: () => {
          if (precheckRequestIdRef.current !== requestId) return;
          setFileItems((prev) =>
            prev.map((item) => ({
              ...item,
              status: 'error',
              errorMessage: intl.formatMessage({
                id: 'marketingAssetsUploadModal.precheckFailed',
                defaultMessage:
                  'Unable to verify this file. Please try again.',
              }),
            })),
          );
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileItemsKey, selectedAssetType, selectedOperator, gameId]);

  const handlePopperRef = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setIsPopperReady(!!el);
  };

  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isFetchingNextPage,
  } = useInfiniteScroll<PartnerSchema>({
    fetchFn: fetchPartners,
    queryKey: [
      'marketing-upload-operators',
      partnerSearchText,
    ] as const,
    enabled: open && isPopperReady,
    params: {
      type: ['operator'],
      ...(partnerSearchText ? { search: partnerSearchText } : {}),
    },
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 20,
    scrollThreshold: 200,
  });

  const assetTypeItems = mapConfigToOptions(
    FileTypesMarketing,
    filtersData?.marketing_assets_file_type?.map((item) => ({
      name: item,
    })),
  );

  const partnerOptions =
    partnersData?.map((partner) => ({
      id: partner.id,
      value: partner.id,
      label: partner.name,
      text: partner.name,
    })) ?? [];

  const selectedOperatorOptions = useMemo(() => {
    if (!selectedOperator) return undefined;
    return [
      {
        id: selectedOperator.id,
        value: selectedOperator.id,
        label: selectedOperator.name,
      },
    ];
  }, [selectedOperator]);

  // Anything the server already rejected would fail the whole batch, so it must block Upload —
  // previously only 'duplicate' did, and a precheck rejection let the user submit a doomed batch.
  const uploadDisabled =
    fileItems.length === 0 ||
    !selectedAssetType ||
    fileItems.some(
      (item) =>
        item.status === 'loading' ||
        item.status === 'duplicate' ||
        item.status === 'inProgress' ||
        item.status === 'error',
    );

  useEffect(() => {
    if (initialFile) {
      setFileItems((prev) => {
        if (prev.some((item) => item.file === initialFile)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: uuidv4(),
            file: initialFile,
            relativePath: initialFile.name,
            status: 'pending',
          },
        ];
      });
    }
  }, [initialFile]);

  const handleNext = () => {
    if (uploadDisabled) return;

    const snapshotItems = [...fileItems];
    const snapshotFiles = snapshotItems.map((item) => item.file);
    const file_type = selectedAssetType as FileType;
    const game_ids = [gameId];
    const partner_ids = selectedOperator ? [selectedOperator.id] : [];

    mutateUploadSessions(
      {
        files: snapshotItems.map(({ file, relativePath }) => ({
          name: relativePath,
          file_type,
          size_bytes: file.size,
          extension: getFileExtension(file.name),
          mime_type: file.type || 'application/octet-stream',
          game_ids,
          partner_ids,
        })),
      },
      {
        onSuccess: (data) => {
          const nextItems = snapshotItems.map((item, index) => {
            const result = data.results.find(
              (r) => r.index === index,
            );
            return {
              ...item,
              ...uploadErrorToFileStatus(result?.error),
            };
          });

          const hasErrors = data.results.some(
            (result) => result.error,
          );

          if (hasErrors) {
            setFileItems(nextItems);
            return;
          }

          const batchId = `batch-${uuidv4()}`;
          const batchFileIds = data.results
            .map((result) => result.file_id)
            .filter(
              (fileId): fileId is number => fileId !== undefined,
            );

          data.results.forEach((result) => {
            const file = snapshotFiles[result.index];
            if (!file) return;

            addUpload({
              file,
              upload_url: result.upload_url ?? '',
              file_id: result.file_id,
              batchId,
              batchFileIds,
              assetDomain: 'marketing_assets',
            });
          });

          onSave();
          handleClose();
        },
        onError: () => {
          setFileItems((prev) =>
            prev.map((item) => ({
              ...item,
              status: 'error',
              errorMessage: intl.formatMessage({
                id: 'marketingAssetsUploadModal.uploadSessionsFailed',
                defaultMessage:
                  'Upload could not be started. Check the file and folder names, then try again.',
              }),
            })),
          );
        },
      },
    );
  };

  const handleClose = () => {
    if (isUploadSessionsPending) return;
    setFileItems([]);
    setSelectedAssetType(undefined);
    setSelectedOperator(null);
    setPartnerSearchText('');
    setIsPopperReady(false);
    scrollContainerRef.current = null;
    onClose();
  };

  return (
    <StyledModal
      open={open}
      onClose={() => {
        if (isUploadSessionsPending) return;
        handleClose();
      }}
      isActionBusy={isUploadSessionsPending}
      title="Upload Marketing Assets"
      actions={[
        <Button
          key="cancel"
          variant="text"
          onClick={handleClose}
          disabled={isUploadSessionsPending}
          loading={isUploadSessionsPending}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Tooltip
          key="next"
          title={
            uploadDisabled ? (
              <FormattedMessage
                id="marketingAssetsUploadModal.completeRequiredFields"
                defaultMessage="In order to continue, please complete all the required fields (marked in asterisk)"
              />
            ) : (
              ''
            )
          }
          disableHoverListener={!uploadDisabled}
          disableFocusListener={!uploadDisabled}
          disableTouchListener={!uploadDisabled}
        >
          <span style={{ display: 'inline-flex' }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={uploadDisabled || isUploadSessionsPending}
              loading={isUploadSessionsPending}
              aria-label={intl.formatMessage({
                id: 'marketingAssetsUploadModal.upload',
                defaultMessage: 'Upload',
              })}
            >
              <FormattedMessage
                id="marketingAssetsUploadModal.upload"
                defaultMessage="Upload"
              />
            </Button>
          </span>
        </Tooltip>,
      ]}
    >
      <Subtitle>
        <FormattedMessage
          id="marketingAssetsUploadModal.subtitle"
          defaultMessage="Choose the asset type and add the files you would like to upload"
        />
      </Subtitle>

      {!gameGroup ? (
        <UngroupedGameNotice role="status">
          <UngroupedGameWarningIcon />
          <span>
            <FormattedMessage
              id="marketingAssetsUploadModal.noGameGroup"
              defaultMessage="This game has no game group, so this upload will only appear on this RTP version — not on the game's other versions. Every game should have a group, so this is worth reporting."
            />
          </span>
        </UngroupedGameNotice>
      ) : null}

      <TwoColumnRow>
        <Column>
          <Label>
            <FormattedMessage
              id="assetType"
              defaultMessage="Asset Type"
            />
            <Asterix>*</Asterix>
          </Label>
          <Select
            multiple={false}
            placeholder={
              isLoadingFilters ? (
                <FormattedMessage
                  id="loading"
                  defaultMessage="Loading..."
                />
              ) : (
                <FormattedMessage id="select" defaultMessage="Select" />
              )
            }
            options={isLoadingFilters ? [] : assetTypeItems}
            value={selectedAssetType}
            onChange={(v) => setSelectedAssetType(v as string)}
            variant="outlined"
            searchEnabled
            size="small"
            disabled={isUploadSessionsPending}
          />
        </Column>
        <Column>
          <Label>
            <FormattedMessage
              id="operator"
              defaultMessage="Operator"
            />
          </Label>
          <Select
            ref={handlePopperRef}
            multiple={false}
            placeholder={
              <FormattedMessage
                id="marketingAssetsUploadModal.selectOperator"
                defaultMessage="Select operator"
              />
            }
            options={partnerOptions}
            selectedOptions={selectedOperatorOptions}
            value={selectedOperator?.id ?? null}
            onChange={(v) => {
              if (v === null) {
                setSelectedOperator(null);
                return;
              }
              const id = v as number;
              const partner = partnersData?.find((p) => p.id === id);
              setSelectedOperator({
                id,
                name: partner?.name ?? String(id),
              });
            }}
            variant="outlined"
            searchEnabled
            size="small"
            loading={isLoadingPartners}
            loadingNextPage={isFetchingNextPage}
            onSearchChange={setPartnerSearchText}
            disableLocalFiltering
            aria-label="Operator"
            disabled={isUploadSessionsPending}
          />
        </Column>
      </TwoColumnRow>

      <DropZoneSection>
        <DropZoneBig
          acceptedExtensions={[...MarketingUploadAcceptedExtensions]}
          busy={isUploadSessionsPending}
          allowDirectories
          onFilesChange={(incoming: PickedFile[]) =>
            setFileItems((prev) => [
              ...prev,
              ...incoming.map(({ file, relativePath }) => ({
                id: uuidv4(),
                file,
                relativePath,
                status: 'pending' as const,
              })),
            ])
          }
        >
          {fileItems.length > 0 ? (
            <>
              <FilesCounter>
                <FormattedMessage
                  id="marketingAssetsUploadModal.filesAdded"
                  defaultMessage="{count, plural, one {# file added} other {# files added}}"
                  values={{ count: fileItems.length }}
                />
              </FilesCounter>
              <FileList>
                {fileItems.map((item) => (
                  <FileListRow key={item.id}>
                    <FileCard>
                      <FileRowContent>
                        <FileIconSlot>
                          <FileIcon
                            fileType={fileNameToIconType(
                              item.file.name,
                            )}
                            size={28}
                          />
                        </FileIconSlot>
                        <FileNameColumn>
                          {folderPartOf(item.relativePath) ? (
                            <FolderPath>
                              {`${folderPartOf(item.relativePath)}/`}
                            </FolderPath>
                          ) : null}
                          <EditableFileName
                            fileName={item.file.name}
                            disabled={isUploadSessionsPending}
                            onSave={(newName) => {
                              setFileItems((prev) =>
                                prev.map((entry) =>
                                  entry.id === item.id
                                    ? {
                                        ...entry,
                                        file: renameFile(
                                          entry.file,
                                          newName,
                                        ),
                                        relativePath: withRenamedFile(
                                          entry.relativePath,
                                          newName,
                                        ),
                                        status: 'pending',
                                        errorMessage: undefined,
                                      }
                                    : entry,
                                ),
                              );
                            }}
                          />
                          {item.status === 'pending' ||
                          item.status === 'loading' ||
                          item.status === 'awaitingAssetType' ? (
                            <FileMeta>
                              {formatFileSize(item.file.size) ?? ''}
                            </FileMeta>
                          ) : (
                            <FileMetaError>
                              <FileMetaWarningIcon
                                width={16}
                                height={16}
                                aria-hidden
                              />
                              {item.status === 'duplicate' ? (
                                <FormattedMessage
                                  id="marketingAssetsUploadModal.duplicateFileError"
                                  defaultMessage="This file already exists. Rename or remove it to continue with the upload."
                                />
                              ) : item.status === 'inProgress' ? (
                                <FormattedMessage
                                  id="marketingAssetsUploadModal.uploadInProgressError"
                                  defaultMessage="This file is still being uploaded. Wait for it to finish, then try again."
                                />
                              ) : (
                                <span>{item.errorMessage}</span>
                              )}
                            </FileMetaError>
                          )}
                        </FileNameColumn>
                        <FileRowEndActions>
                          {item.status === 'loading' ? (
                            <CircularProgress size={16} />
                          ) : item.status === 'awaitingAssetType' ? (
                            <FileStatusBadge state="Waiting" />
                          ) : item.status === 'pending' ||
                            item.status === 'duplicate' ||
                            item.status === 'inProgress' ? (
                            <FileStatusBadge
                              state={
                                item.status === 'duplicate'
                                  ? 'Duplicate'
                                  : item.status === 'inProgress'
                                    ? 'ActionNeeded'
                                    : 'Ready'
                              }
                            />
                          ) : null}
                          <RemoveFileButton
                            type="button"
                            disabled={isUploadSessionsPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFileItems((prev) =>
                                prev.filter((f) => f.id !== item.id),
                              );
                            }}
                            aria-label={intl.formatMessage({
                              id: 'marketingAssetsUploadModal.removeFile',
                              defaultMessage: 'Remove file',
                            })}
                          >
                            <DeleteCircleIcon
                              width={24}
                              height={24}
                              aria-hidden
                            />
                          </RemoveFileButton>
                        </FileRowEndActions>
                      </FileRowContent>
                    </FileCard>
                  </FileListRow>
                ))}
              </FileList>
            </>
          ) : null}
        </DropZoneBig>
      </DropZoneSection>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)({
  width: '90%',
  maxWidth: '800px',
});

const TwoColumnRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const DropZoneSection = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
}));

const Column = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
}));

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 400,
  color: theme.palette.textSecondaryDarkGrey,
  marginBottom: theme.spacing(2),
}));

const FilesCounter = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
  marginBottom: theme.spacing(1.5),
}));

const FileList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const FileListRow = styled('li')({
  margin: 0,
  padding: 0,
});

const FileCard = styled('div')(({ theme }) => ({
  padding: '16px',
  borderRadius: theme.radii.md,
  border: `1px solid ${theme.palette.grey200}`,
}));

const FileRowContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  minWidth: 0,
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.textPrimaryBlack,
}));

const FileIconSlot = styled('span')(({ theme }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  marginTop: theme.spacing(0.25),
}));

const FileNameColumn = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const FileMeta = styled('span')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.secDefault,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/** Shown in every row state, unlike the size meta. */
const FolderPath = styled('span')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.black40o,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const FileMetaError = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.textWarning,
  '& span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const UngroupedGameNotice = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: '12px 16px',
  borderRadius: theme.radii.md,
  background: theme.palette.yellow50,
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.textPrimaryBlack,
}));

const UngroupedGameWarningIcon = styled(WarningTriangleIcon)(
  ({ theme }) => ({
    flexShrink: 0,
    display: 'block',
    marginTop: '1px',
    color: theme.palette.textWarning,
    '& path': {
      stroke: 'currentColor',
    },
  }),
);

const FileMetaWarningIcon = styled(WarningTriangleIcon)({
  flexShrink: 0,
  display: 'block',
  '& path': {
    stroke: 'currentColor',
  },
});

const FileRowEndActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  alignSelf: 'center',
  gap: theme.spacing(0.5),
}));

const RemoveFileButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  margin: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  borderRadius: theme.radii.xs,
  color: theme.palette.iconGrey,
  '& path': {
    stroke: 'currentColor',
  },
  '&:hover:not(:disabled)': {
    color: theme.palette.grey600,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary500}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.45,
  },
}));

export default MarketingAssetsUploadModal;
